import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import {
  AccountBalanceWallet as ReconciliationIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ReceiptLong as PaymentIcon,
  Refresh as RefreshIcon,
  TrendingDown as PendingIcon,
  TrendingUp as CompletedIcon,
  WarningAmber as WarningIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { paymentService } from '../../services/paymentService';
import { formatCurrency, formatDateShort } from '../../utils/formatters';

// Helper function to get translated status
const getTranslatedStatus = (statusKey, statusType, t) => {
  try {
    if (statusType === 'settlement') {
      return t(`settlementMode.${statusKey}`, statusKey);
    } else if (statusType === 'reconciliation') {
      return t(`reconciliationStatus.${statusKey}`, statusKey);
    } else {
      return t(`status.${statusKey}`, statusKey);
    }
  } catch (e) {
    return statusKey;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'paid_out':
    case 'cash_settled':
    case 'completed':
      return 'success';
    case 'pending_payout':
    case 'cash_commission_due':
      return 'warning';
    case 'under_dispute':
    case 'penalty_pending':
      return 'error';
    default:
      return 'default';
  }
};

const formatMoney = (amount, currency) => {
  if (!currency) {
    return Number(amount || 0).toFixed(2);
  }

  return formatCurrency(amount || 0, currency);
};

const MetricCard = ({ icon, label, value, color = 'primary' }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
        <Box color={`${color}.main`} sx={{ display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Stack>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const SummaryLine = ({ label, value, color = 'text.primary' }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color }}>
      {value}
    </Typography>
  </Box>
);

const OwnerReservationReconciliationWidget = ({ currency = null, onError }) => {
  const { t } = useTranslation('financial');
  const theme = useTheme();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [isMainExpanded, setIsMainExpanded] = useState(true);

  const loadReconciliation = useCallback(async (page = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      setError(null);

      const response = await paymentService.getOwnerReservationReconciliation({
        page,
        limit: 10,
        currency
      });

      const payload = response.data;
      setSummary(payload.global_summary || payload.summary || null);
      setPagination(payload.pagination || null);
      setItems((previousItems) => (reset ? payload.items || [] : [...previousItems, ...(payload.items || [])]));
    } catch (serviceError) {
      setError(serviceError.message || t('errors.loadRevenuesFailed', 'Erreur lors du chargement du rapprochement'));
      if (onError) {
        onError(serviceError);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [currency, onError, t]);

  useEffect(() => {
    loadReconciliation(1, true);
  }, [loadReconciliation]);

  const handleRefresh = () => {
    loadReconciliation(1, true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Main Accordion Wrapper */}
      <Accordion expanded={isMainExpanded} onChange={() => setIsMainExpanded(!isMainExpanded)} disableGutters sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.info.main} 90%)`,
            color: 'white',
            '&:hover': {
              background: `linear-gradient(45deg, ${theme.palette.info.main} 30%, ${theme.palette.primary.main} 90%)`
            },
            '& .MuiAccordionSummary-content': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              gap: 2,
              my: 1.5
            }
          }}
        >
          {/* Left side: Icon + Title + Subtitle */}
          <Box display="flex" alignItems="center" gap={2}>
            <ReconciliationIcon sx={{ color: 'white', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>
                {t('owner.reconciliation.title', 'Rapprochement par réservation')}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                {t(
                  'owner.reconciliation.subtitle',
                  'Paiements, payouts, payins, litiges et pénalités'
                )}
              </Typography>
            </Box>
          </Box>

          {/* Right side: Currency Chip + Refresh Button */}
          <Box display="flex" alignItems="center" gap={1} onClick={(e) => e.stopPropagation()}>
            {currency && (
              <Chip label={currency} color="default" variant="filled" sx={{ bgcolor: 'rgba(255,255,255,0.9)', color: theme.palette.primary.main }} />
            )}
            <IconButton onClick={(e) => { e.stopPropagation(); handleRefresh(); }} sx={{ color: 'white' }} size="small">
              <RefreshIcon />
            </IconButton>
          </Box>
        </AccordionSummary>

        {/* Content inside AccordionDetails */}
        <AccordionDetails sx={{ pt: 3, pb: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {summary && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <MetricCard
              icon={<PaymentIcon />}
              label={t('owner.reconciliation.expectedNet', 'Net attendu propriétaire')}
              value={formatMoney(summary.payment_breakdown?.expected_owner_total, summary.payment_breakdown?.currency)}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MetricCard
              icon={<CompletedIcon />}
              label={t('owner.reconciliation.payoutCompleted', 'Payouts complétés')}
              value={formatMoney(summary.payout_breakdown?.completed_net, summary.payout_breakdown?.currency)}
              color="success"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MetricCard
              icon={<PendingIcon />}
              label={t('owner.reconciliation.payoutPending', 'Payouts / payins en attente')}
              value={`${formatMoney(summary.payout_breakdown?.pending_net, summary.payout_breakdown?.currency)} • ${formatMoney(summary.payin_breakdown?.pending_total, summary.payin_breakdown?.currency)}`}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MetricCard
              icon={<WarningIcon />}
              label={t('owner.reconciliation.deposits', 'Dépôts (total autorisés)')}
              value={formatMoney(summary.deposit_breakdown?.total_amount, summary.deposit_breakdown?.currency)}
              color="info"
            />
            </Grid>
            </Grid>
          )}

          {items.length === 0 ? (
            <Alert severity="info">
              {t('owner.reconciliation.empty', 'Aucune réservation à rapprocher pour ce filtre.')}
            </Alert>
          ) : (
            <Stack spacing={1.5}>
          {items.map((item) => (
            <Accordion key={item.reservation_id} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {item.listing?.title}
                    </Typography>
                    <Chip size="small" label={getTranslatedStatus(item.settlement_mode, 'settlement', t)} variant="outlined" />
                    <Chip
                      size="small"
                      color={getStatusColor(item.reconciliation?.status)}
                      label={getTranslatedStatus(item.reconciliation?.status, 'reconciliation', t)}
                    />
                    <Chip size="small" label={getTranslatedStatus(item.reservation_status, 'status', t)} />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {item.renter?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {`${formatDateShort(item.start_date)} - ${formatDateShort(item.end_date)}`}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatMoney(item.reconciliation?.expected_owner_amount, item.reconciliation?.expected_owner_currency)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('owner.reconciliation.payoutShort', 'versé')} {formatMoney(item.reconciliation?.payout_completed_net, item.reconciliation?.payout_currency)}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                          {t('owner.reconciliation.sections.payments', 'Paiements source')}
                        </Typography>
                        <Stack spacing={1}>
                          <SummaryLine label={t('types.rental', 'Service')} value={formatMoney(item.payment_summary?.completed?.rental, item.payment_summary?.currency)} />
                          <SummaryLine label={t('types.tax', 'Taxes')} value={formatMoney(item.payment_summary?.completed?.tax, item.payment_summary?.currency)} />
                          <SummaryLine label={t('types.commission', 'Commission')} value={formatMoney(item.payment_summary?.completed?.commission, item.payment_summary?.currency)} />
                          <SummaryLine
                            label={t('owner.reconciliation.labels.depositStatus', 'Dépôt (caution)')}
                            value={
                              item.deposit
                                ? `${formatMoney(item.deposit.amount, item.deposit.currency || item.payment_summary?.currency)} — ${getTranslatedStatus(item.deposit.status, 'status', t)}`
                                : '—'
                            }
                          />
                          <SummaryLine label={t('types.dispute_refund', 'Remboursement litige')} value={formatMoney(item.payment_summary?.completed?.dispute_refund, item.payment_summary?.currency)} color="error.main" />
                          <SummaryLine label={t('types.damage_compensation', 'Compensation dommages')} value={formatMoney(item.payment_summary?.completed?.damage_compensation, item.payment_summary?.currency)} color="success.main" />
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                          {t('owner.reconciliation.sections.payouts', 'Payouts Stripe / payins cash')}
                        </Typography>
                        <Stack spacing={1}>
                          <SummaryLine label={t('owner.reconciliation.payoutCompleted', 'Payout complété')} value={formatMoney(item.payout_summary?.completed_net, item.payout_summary?.currency)} color="success.main" />
                          <SummaryLine label={t('owner.reconciliation.payoutPending', 'Payout en attente')} value={formatMoney(item.payout_summary?.pending_net, item.payout_summary?.currency)} color="warning.main" />
                          <SummaryLine label={t('types.principal_completed', 'Principal complété')} value={formatMoney(item.payout_summary?.principal_completed_net, item.payout_summary?.currency)} />
                          <SummaryLine label={t('types.compensation_completed', 'Compensation dommages payée')} value={formatMoney(item.payout_summary?.compensation_completed_net, item.payout_summary?.currency)} />
                          <SummaryLine label={t('types.penalty_deductions', 'Ponctions pénalités')} value={formatMoney(item.payout_summary?.penalty_deductions_total, item.payout_summary?.currency)} color="error.main" />
                          <Divider sx={{ my: 0.5 }} />
                          <SummaryLine label={t('types.commission_cash_due', 'Commission cash due')} value={formatMoney(item.payin_summary?.pending_total, item.payin_summary?.currency)} color="warning.main" />
                          <SummaryLine label={t('types.commission_cash_paid', 'Commission cash paid')} value={formatMoney(item.payin_summary?.completed_total, item.payin_summary?.currency)} color="success.main" />
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                          {t('owner.reconciliation.sections.disputes', 'Litiges et pénalités')}
                        </Typography>
                        <Stack spacing={1}>
                          <SummaryLine label={t('dispute.openCount', 'Litiges ouverts')} value={String(item.dispute_summary?.open_count || 0)} />
                          <SummaryLine label={t('compensationTypes.owner_compensation', 'Compensation propriétaire')} value={formatMoney(item.dispute_summary?.owner_compensation_total, item.reconciliation?.expected_owner_currency)} color="success.main" />
                          <SummaryLine label={t('compensationTypes.renter_refund', 'Remboursement client')} value={formatMoney(item.dispute_summary?.renter_refund_total, item.reconciliation?.expected_owner_currency)} color="error.main" />
                          <Divider sx={{ my: 0.5 }} />
                          <SummaryLine label={t('penalty.chargedToOwner', 'Pénalités charge propriétaire')} value={formatMoney(item.penalty_summary?.charged_to_owner_total, item.penalty_summary?.currency)} color="error.main" />
                          <SummaryLine label={t('penalty.compensated', 'Compensation vers propriétaire')} value={formatMoney(item.penalty_summary?.compensated_to_owner_total, item.penalty_summary?.currency)} color="success.main" />
                          <SummaryLine label={t('penalty.remaining', 'Reste à collecter')} value={formatMoney(item.penalty_summary?.remaining_total, item.penalty_summary?.currency)} color="warning.main" />
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                          {t('owner.reconciliation.sections.finalState', 'État final')}
                        </Typography>
                        <Stack spacing={1}>
                          <SummaryLine label={t('owner.reconciliation.expectedNet', 'Net attendu')} value={formatMoney(item.reconciliation?.expected_owner_amount, item.reconciliation?.expected_owner_currency)} />
                          <SummaryLine label={t('types.payout_completed_net', 'Net payout complété')} value={formatMoney(item.reconciliation?.payout_completed_net, item.reconciliation?.payout_currency)} />
                          <SummaryLine label={t('types.payout_pending_net', 'Net payout en attente')} value={formatMoney(item.reconciliation?.payout_pending_net, item.reconciliation?.payout_currency)} />
                          <SummaryLine label={t('types.penalty_effect', 'Impact pénalités')} value={formatMoney(item.reconciliation?.penalty_net_effect, item.reconciliation?.penalty_currency)} color={item.reconciliation?.penalty_net_effect >= 0 ? 'success.main' : 'error.main'} />
                          <Divider sx={{ my: 0.5 }} />
                          <SummaryLine label={t('owner.reconciliation.labels.deposit', 'Dépôt')} value={item.deposit ? `${formatMoney(item.deposit.amount, item.deposit.currency || item.reconciliation?.expected_owner_currency)} • ${getTranslatedStatus(item.deposit.status, 'status', t)}` : '—'} />
                          <SummaryLine label={t('reservationStatuses.reservation', 'Réservation')} value={item.reservation_id} />
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
            </Stack>
          )}

          {pagination?.has_next_page && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button onClick={() => loadReconciliation((pagination.current_page || 1) + 1, false)} disabled={loadingMore}>
                {loadingMore ? <CircularProgress size={20} /> : t('common:loadMore', 'Charger plus')}
              </Button>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default OwnerReservationReconciliationWidget;